import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useContext,
  useRef,
} from "react";
import { motion } from "framer-motion";
import Select from "react-select";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { fetchWithAuth } from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";
import "../../App.css";
import { swalError } from "../../utils/swalAlert";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Constants - Generate batch options (last 10 years from current)
const generateBatchOptions = () => {
  const currentYear = new Date().getFullYear();
  const plusFiveYears = currentYear + 5;
  const options = [];
  for (let year = plusFiveYears; year >= currentYear - 5; year--) {
    options.push({ value: year.toString(), label: year.toString() });
  }
  return options;
};

// ============================================================================
// SEMESTER ATTAINMENT CHART (Bar chart aggregated by semester/courseLevel)
// ============================================================================
const SemesterAttainmentChart = ({ semesters, onSemesterClick }) => {
  if (!semesters || semesters.length === 0) {
    return (
      <div className="chart-container-empty">
        <div className="empty-chart-message">
          <span className="empty-icon">📊</span>
          <p>No semester data available</p>
        </div>
      </div>
    );
  }

  const getBarColor = (value) => {
    if (value >= 2.5) return "rgba(34, 197, 94, 0.8)";
    if (value >= 2.0) return "rgba(59, 130, 246, 0.8)";
    if (value >= 1.5) return "rgba(251, 191, 36, 0.8)";
    return "rgba(239, 68, 68, 0.8)";
  };

  const chartData = {
    labels: semesters.map((s) => s.semester),
    datasets: [
      {
        label: "Semester Attainment",
        data: semesters.map((s) => parseFloat(s.avgAttainment?.toFixed(2))),
        backgroundColor: semesters.map((s) => getBarColor(s.avgAttainment)),
        borderColor: semesters.map((s) =>
          getBarColor(s.avgAttainment).replace("0.8", "1")
        ),
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0 && onSemesterClick) {
        const index = elements[0].index;
        onSemesterClick(semesters[index]);
      }
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Semester Level Attainment (Click to view details)",
        font: { size: 16, weight: "bold" },
        color: "#1f2937",
      },
      tooltip: {
        callbacks: {
          title: (context) => semesters[context[0].dataIndex]?.semester || "",
          label: (context) => `Attainment: ${context.parsed.y?.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        max: 3,
        title: { display: true, text: "Attainment Level" },
      },
    },
  };

  return (
    <motion.div
      className="course-attainment-chart-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="chart-legend">
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(34, 197, 94, 0.8)" }}
          ></span>
          <span>Excellent (≥2.5)</span>
        </div>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(59, 130, 246, 0.8)" }}
          ></span>
          <span>Good (2.0-2.5)</span>
        </div>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(251, 191, 36, 0.8)" }}
          ></span>
          <span>Average (1.5-2.0)</span>
        </div>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(239, 68, 68, 0.8)" }}
          ></span>
          <span>Needs Improvement (&lt;1.5)</span>
        </div>
      </div>
      <div className="chart-wrapper" style={{ cursor: "pointer" }}>
        <Bar data={chartData} options={options} />
      </div>
    </motion.div>
  );
};

// ============================================================================
// COURSE ATTAINMENT CHART (Bar chart showing attainment by course)
// ============================================================================
const CourseAttainmentChart = ({ courses, onCourseClick }) => {
  if (!courses || courses.length === 0) {
    return (
      <div className="chart-container-empty">
        <div className="empty-chart-message">
          <span className="empty-icon">📊</span>
          <p>No course data available</p>
        </div>
      </div>
    );
  }

  const getBarColor = (value) => {
    if (value >= 2.5) return "rgba(34, 197, 94, 0.8)";
    if (value >= 2.0) return "rgba(59, 130, 246, 0.8)";
    if (value >= 1.5) return "rgba(251, 191, 36, 0.8)";
    return "rgba(239, 68, 68, 0.8)";
  };

  const chartData = {
    labels: courses.map((c) => c.courseCode || c.courseName.substring(0, 15)),
    datasets: [
      {
        label: "Course Attainment",
        data: courses.map((c) => parseFloat(c.avgAttainment?.toFixed(2))),
        backgroundColor: courses.map((c) => getBarColor(c.avgAttainment)),
        borderColor: courses.map((c) =>
          getBarColor(c.avgAttainment).replace("0.8", "1")
        ),
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0 && onCourseClick) {
        const index = elements[0].index;
        onCourseClick(courses[index]);
      }
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Course Level Attainment (Click to view details)",
        font: { size: 16, weight: "bold" },
        color: "#1f2937",
      },
      tooltip: {
        callbacks: {
          title: (context) => courses[context[0].dataIndex]?.courseName || "",
          label: (context) => `Attainment: ${context.parsed.y?.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        max: 3,
        title: { display: true, text: "Attainment Level" },
      },
    },
  };

  return (
    <motion.div
      className="course-attainment-chart-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="chart-legend">
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(34, 197, 94, 0.8)" }}
          ></span>
          <span>Excellent (≥2.5)</span>
        </div>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(59, 130, 246, 0.8)" }}
          ></span>
          <span>Good (2.0-2.5)</span>
        </div>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(251, 191, 36, 0.8)" }}
          ></span>
          <span>Average (1.5-2.0)</span>
        </div>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(239, 68, 68, 0.8)" }}
          ></span>
          <span>Needs Improvement (&lt;1.5)</span>
        </div>
      </div>
      <div className="chart-wrapper" style={{ cursor: "pointer" }}>
        <Bar data={chartData} options={options} />
      </div>
    </motion.div>
  );
};

// ============================================================================
// PO/PSO ATTAINMENT CHART (single bar with target-based colors)
// ============================================================================
const POPSOAttainmentChart = ({ poData, title = "PO & PSO Attainment" }) => {
  if (!poData || poData.length === 0) return null;

  const chartData = {
    labels: poData.map((p) => p.code),
    datasets: [
      {
        label: "Attainment",
        data: poData.map((p) => parseFloat((p.attainment || 0)?.toFixed(2))),
        backgroundColor: poData.map((p) =>
          (p.attainment || 0) >= (p.target || 0)
            ? "rgba(34, 197, 94, 0.8)"
            : "rgba(239, 68, 68, 0.8)"
        ),
        borderColor: poData.map((p) =>
          (p.attainment || 0) >= (p.target || 0)
            ? "rgba(22, 163, 74, 1)"
            : "rgba(220, 38, 38, 1)"
        ),
        borderWidth: 2,
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: title,
        font: { size: 16, weight: "bold" },
        color: "#1f2937",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const po = poData[context.dataIndex];
            const description = po.description || "";
            // Truncate long descriptions for better tooltip display
            const truncatedDesc =
              description.length > 60
                ? description.substring(0, 60) + "..."
                : description;
            const status =
              (po.attainment || 0) >= (po.target || 0)
                ? "✓ Target Met"
                : "✗ Below Target";
            return [
              truncatedDesc,
              `Attainment: ${context.parsed.y?.toFixed(2)}`,
              `Target: ${po.target || 0}`,
              status,
            ];
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        max: 3,
        title: { display: true, text: "Attainment Level" },
      },
    },
  };

  return (
    <motion.div
      className="course-attainment-chart-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="chart-legend">
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(34, 197, 94, 0.8)" }}
          ></span>
          <span>Target Met (≥ Target)</span>
        </div>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: "rgba(239, 68, 68, 0.8)" }}
          ></span>
          <span>Below Target</span>
        </div>
      </div>
      <div className="chart-wrapper">
        <Bar data={chartData} options={options} />
      </div>
    </motion.div>
  );
};

// ============================================================================
// PO/PSO MULTI-BAR CHART (grouped bars per batch)
// ============================================================================
const POPSOMultiBarChart = ({
  chartData,
  title = "PO & PSO Attainment by Batch",
}) => {
  if (!chartData || !chartData.datasets || chartData.datasets.length === 0)
    return null;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: title,
        font: { size: 16, weight: "bold" },
        color: "#1f2937",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label;
            const attainment = context.parsed.y;
            return `${datasetLabel}: ${attainment?.toFixed(2)}`;
          },
          afterLabel: (context) => {
            if (!chartData.poDescriptions) return [];
            const po = chartData.poDescriptions[context.dataIndex];
            if (!po) return [];

            const description = po.description || "";
            // Truncate long descriptions for better tooltip display
            const truncatedDesc =
              description.length > 60
                ? description.substring(0, 60) + "..."
                : description;
            const target = po.target || 0;
            const attainment = context.parsed.y;
            const status =
              attainment >= target ? "✓ Target Met" : "✗ Below Target";
            return [truncatedDesc, `Target: ${target}`, status];
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        max: 3,
        title: { display: true, text: "Attainment Level" },
      },
    },
  };

  const data = {
    labels: chartData.labels,
    datasets: chartData.datasets,
  };

  return (
    <motion.div
      className="course-attainment-chart-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="chart-legend">
        {chartData.datasets.map((ds, idx) => (
          <div key={idx} className="legend-item">
            <span
              className="legend-color"
              style={{ backgroundColor: ds.backgroundColor }}
            ></span>
            <span>{ds.label}</span>
          </div>
        ))}
      </div>
      <div className="chart-wrapper">
        <Bar data={data} options={options} />
      </div>
    </motion.div>
  );
};

// ============================================================================
// CO ATTAINMENT TABLE
// ============================================================================
const COAttainmentTable = ({ attainment, overallAverage, courseCode }) => {
  if (!attainment || attainment.length === 0) return null;

  return (
    <div className="table-wrapper glass">
      <table className="attainment-table">
        <thead>
          <tr>
            <th
              colSpan={6}
              style={{
                background: "#ffde17",
                color: "#1f2937",
                textAlign: "center",
                fontSize: "1.25rem",
              }}
            >
              CO Attainment
            </th>
          </tr>
          <tr>
            <th style={{ background: "#ffde17", color: "black" }}>CO Level</th>
            <th style={{ background: "#ffde17", color: "black" }}>
              Internal Attainment
            </th>
            <th style={{ background: "#ffde17", color: "black" }}>
              External Attainment
            </th>
            <th style={{ background: "#ffde17", color: "black" }}>
              Direct CO Attainment
            </th>
            <th style={{ background: "#ffde17", color: "black" }}>
              Indirect CO Attainment
            </th>
            <th style={{ background: "#ffde17", color: "black" }}>
              Total CO Attainment
            </th>
          </tr>
        </thead>
        <tbody>
          {attainment.map((item, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? "even-row" : "odd-row"}>
              <td style={{ textAlign: "center", fontWeight: 600 }}>
                {item.coLevel}
              </td>
              <td style={{ textAlign: "center" }}>
                {(item.internalAttainment || 0)?.toFixed(2)}
              </td>
              <td style={{ textAlign: "center" }}>
                {(item.externalAttainment || 0)?.toFixed(2)}
              </td>
              <td style={{ textAlign: "center" }}>
                {(item.directCOAttainment || 0)?.toFixed(2)}
              </td>
              <td style={{ textAlign: "center" }}>
                {(item.indirectCOAttainment || 0)?.toFixed(2)}
              </td>
              <td style={{ textAlign: "center", fontWeight: 600 }}>
                {(item.totalCOAttainment || 0)?.toFixed(2)}
              </td>
            </tr>
          ))}
          <tr className="summary-row" style={{ backgroundColor: "#d4e9f1" }}>
            <td colSpan={5} style={{ textAlign: "center", fontWeight: 700 }}>
              {courseCode || "-"}
            </td>
            <td style={{ textAlign: "center", fontWeight: 700 }}>
              {(overallAverage || 0)?.toFixed(2)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// PO & PSO ATTAINMENT SUMMARY TABLE (separate table as per design)
// ============================================================================
const POPSOAttainmentSummaryTable = ({ courseCOPOMapping, coAttainment }) => {
  if (!courseCOPOMapping || courseCOPOMapping.length === 0) return null;

  const allPOs = [
    "PO1",
    "PO2",
    "PO3",
    "PO4",
    "PO5",
    "PO6",
    "PO7",
    "PO8",
    "PO9",
    "PO10",
    "PO11",
    "PO12",
    "PSO 1",
    "PSO 2",
    "PSO 3",
  ];

  const calculateAverage = (po) => {
    const normalizedPO = po.replace("PSO ", "PS").replace("PSO", "PS");
    const weights = courseCOPOMapping
      .filter((item) => item.POCode === normalizedPO)
      .map((item) => item.weight);
    if (weights.length === 0) return "-";
    return (weights.reduce((a, b) => a + b, 0) / weights.length)?.toFixed(2);
  };

  const calculatePOAttainment = (po) => {
    const avg = calculateAverage(po);
    if (avg === "-") return "-";
    return ((coAttainment || 0) * (parseFloat(avg) / 3))?.toFixed(2);
  };

  return (
    <div className="table-wrapper glass" style={{ marginTop: "2rem" }}>
      <table className="attainment-table">
        <thead>
          <tr>
            <th
              colSpan={allPOs.length + 1}
              style={{
                background: "#ffde17",
                color: "#1f2937",
                textAlign: "center",
                fontSize: "1.25rem",
                fontWeight: 700,
              }}
            >
              PO & PSO Attainment
            </th>
          </tr>
          <tr>
            <th style={{ background: "#ffde17", color: "black" }}></th>
            {allPOs.map((po) => (
              <th
                key={po}
                style={{
                  background: "#ffde17",
                  color: "black",
                  minWidth: "50px",
                  fontWeight: 700,
                }}
              >
                {po}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            <td style={{ fontWeight: 700, textAlign: "left" }}>
              PO & PSO Attainment
            </td>
            {allPOs.map((po) => (
              <td
                key={`att-${po}`}
                style={{ textAlign: "center", fontWeight: 600 }}
              >
                {calculatePOAttainment(po)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// CO-PO MAPPING TABLE (with POs & PSOs subheader like image)
// ============================================================================
const COPOMappingTable = ({ courseCOPOMapping, courseCode }) => {
  if (!courseCOPOMapping || courseCOPOMapping.length === 0) return null;

  const allPOs = [
    "PO1",
    "PO2",
    "PO3",
    "PO4",
    "PO5",
    "PO6",
    "PO7",
    "PO8",
    "PO9",
    "PO10",
    "PO11",
    "PO12",
    "PSO 1",
    "PSO 2",
    "PSO 3",
  ];
  const cos = [...new Set(courseCOPOMapping.map((item) => item.COCode))].sort();

  const getWeight = (co, po) => {
    const normalizedPO = po.replace("PSO ", "PS").replace("PSO", "PS");
    const mapping = courseCOPOMapping.find(
      (item) => item.COCode === co && item.POCode === normalizedPO
    );
    return mapping ? mapping.weight : "-";
  };

  const calculateAverage = (po) => {
    const normalizedPO = po.replace("PSO ", "PS").replace("PSO", "PS");
    const weights = courseCOPOMapping
      .filter((item) => item.POCode === normalizedPO)
      .map((item) => item.weight);
    if (weights.length === 0) return "-";
    return (weights.reduce((a, b) => a + b, 0) / weights.length)?.toFixed(2);
  };

  return (
    <div className="table-wrapper glass" style={{ marginTop: "2rem" }}>
      <table className="attainment-table">
        <thead>
          <tr>
            <th
              colSpan={allPOs.length + 1}
              style={{
                background: "#ffde17",
                color: "#1f2937",
                textAlign: "center",
                fontSize: "1.25rem",
                fontWeight: 700,
              }}
            >
              CO-PO & CO-PSO Mapping
            </th>
          </tr>
          <tr>
            <th
              rowSpan={2}
              style={{ background: "#ffde17", color: "black", fontWeight: 700 }}
            >
              COs
            </th>
            <th
              colSpan={allPOs.length}
              style={{
                background: "#ffde17",
                color: "black",
                textAlign: "center",
                fontWeight: 700,
              }}
            >
              POs & PSOs
            </th>
          </tr>
          <tr>
            {allPOs.map((po) => (
              <th
                key={po}
                style={{
                  background: "#ffde17",
                  color: "black",
                  minWidth: "40px",
                  textAlign: "center",
                }}
              >
                {po}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cos.map((co, idx) => (
            <tr key={co} className={idx % 2 === 0 ? "even-row" : "odd-row"}>
              <td style={{ fontWeight: 600, textAlign: "center" }}>{co}</td>
              {allPOs.map((po) => (
                <td key={`${co}-${po}`} style={{ textAlign: "center" }}>
                  {getWeight(co, po)}
                </td>
              ))}
            </tr>
          ))}
          <tr style={{ backgroundColor: "#ffde17" }}>
            <td style={{ fontWeight: 700, textAlign: "center" }}>
              {courseCode || "-"}
            </td>
            {allPOs.map((po) => (
              <td
                key={`avg-${po}`}
                style={{ textAlign: "center", fontWeight: 600 }}
              >
                {calculateAverage(po)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// CO ATTAINMENT SUMMARY TABLE (simple 2-column table like image)
// ============================================================================
const COAttainmentSummaryTable = ({ coAttainment }) => {
  return (
    <div className="table-wrapper glass" style={{ marginTop: "1rem" }}>
      <table className="attainment-table">
        <thead>
          <tr>
            <th
              colSpan={2}
              style={{
                background: "#ffde17",
                color: "#1f2937",
                textAlign: "center",
                fontSize: "1rem",
                fontWeight: 700,
                padding: "0.5rem 1rem",
              }}
            >
              Total CO Attainment through Direct & Indirect Assessment
            </th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            <td
              style={{
                fontWeight: 600,
                textAlign: "center",
                padding: "0.5rem 2rem",
              }}
            >
              CO Attainment
            </td>
            <td
              style={{
                fontWeight: 700,
                textAlign: "center",
                padding: "0.5rem 2rem",
              }}
            >
              {(coAttainment || 0)?.toFixed(2)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// PO/PSO ATTAINMENT TABLE
// ============================================================================
const POPSOAttainmentTable = ({
  classesData,
  programOutcomes = [],
  psOutcomes = [],
  padding = "0",
  indirectAttainmentData = [],
  directPOWeightage = 0.9,
  indirectPOWeightage = 0.1,
}) => {
  const data = useMemo(() => {
    if (!classesData || classesData.length === 0) return null;

    const standardPOs = [
      "PO1",
      "PO2",
      "PO3",
      "PO4",
      "PO5",
      "PO6",
      "PO7",
      "PO8",
      "PO9",
      "PO10",
      "PO11",
      "PO12",
    ];
    const standardPSOs = ["PSO1", "PSO2", "PSO3"];
    const allCodes = [...standardPOs, ...standardPSOs];

    // Get target attainments from program data
    const targets = {};
    programOutcomes.forEach((po) => {
      targets[po.code] = po.targetAttainment || 0;
    });
    psOutcomes.forEach((pso) => {
      targets[pso.code] = pso.targetAttainment || 0;
    });

    // Calculate attainment for each class
    const classAttainments = classesData
      .filter((cls) => {
        // Only include classes that have CO-PO mapping AND actual attainment data
        const mapping = cls.courseCOPOMapping || [];
        const hasMapping = mapping.length > 0;
        const hasAttainment = (cls.overallAverage || 0) > 0;
        return hasMapping && hasAttainment;
      })
      .map((cls) => {
        const poAttainment = {};
        const mapping = cls.courseCOPOMapping || [];
        const overallAvg = cls.overallAverage || 0;

        allCodes.forEach((poCode) => {
          const targetPO = poCode.startsWith("PSO")
            ? poCode.replace("PSO", "PS")
            : poCode;
          const weights = mapping
            .filter((item) => {
              const itemPO = (item.POCode || "")
                .replace("PSO", "PS")
                .replace(" ", "");
              return itemPO === targetPO;
            })
            .map((item) => item.weight);

          if (weights.length === 0) {
            poAttainment[poCode] = null;
          } else {
            const avgWeight =
              weights.reduce((a, b) => a + b, 0) / weights.length;
            poAttainment[poCode] = overallAvg * (avgWeight / 3);
          }
        });

        return {
          className: cls.className,
          classId: cls.classId,
          courseName: cls.courseName,
          courseLevel: cls.courseLevel,
          period: `${cls.periodTerm || ""} ${cls.periodYear || ""}`.trim(),
          attainment: poAttainment,
        };
      });

    // Identify unique batches from the displayed classes
    const involvedBatches = [
      ...new Set(classesData.map((c) => String(c.batch))),
    ];

    // Filter indirect attainment records matching these batches
    const relevantIndirectData = indirectAttainmentData.filter((item) =>
      involvedBatches.includes(String(item.batch))
    );

    const averages = {};
    const indirectAverages = {};

    allCodes.forEach((poCode) => {
      const values = classAttainments
        .map((c) => c.attainment[poCode])
        .filter((v) => v !== null);
      averages[poCode] =
        values.length > 0
          ? values.reduce((a, b) => a + b, 0) / values.length
          : null;

      // Indirect attainment from data
      const targetPO = poCode.startsWith("PSO")
        ? poCode.replace("PSO", "PS")
        : poCode;

      const indirectValues = relevantIndirectData
        .map((record) => {
          const attainments = record.attainments || [];
          const found = attainments.find((a) => {
            const itemPO = (a.poCode || "")
              .replace("PSO", "PS")
              .replace(" ", "");
            return itemPO === targetPO || a.poCode === poCode;
          });
          return found ? parseFloat(found.attainment) : null;
        })
        .filter((v) => v !== null && !isNaN(v));

      indirectAverages[poCode] =
        indirectValues.length > 0
          ? indirectValues.reduce((a, b) => a + b, 0) / indirectValues.length
          : null;
    });

    return { classAttainments, allCodes, averages, indirectAverages, targets };
  }, [classesData, programOutcomes, psOutcomes, indirectAttainmentData]);

  if (!data) {
    return (
      <div className="chart-container-empty">
        <div className="empty-chart-message">
          <span className="empty-icon">📋</span>
          <p>No PO/PSO data available</p>
        </div>
      </div>
    );
  }

  const { classAttainments, allCodes, averages, indirectAverages, targets } =
    data;
  const formatValue = (v) =>
    v === null || v === undefined ? "-" : v?.toFixed(2);

  const getCellStyle = (value, code) => {
    const target = targets[code] || 0;
    if (value === null || value === undefined || target === 0) return {};
    return {
      backgroundColor:
        value >= target ? "rgba(34, 197, 94, 0.2)" : "rgba(239, 68, 68, 0.2)",
      color: value >= target ? "#166534" : "#dc2626",
      fontWeight: 600,
    };
  };

  return (
    <div
      style={{ width: "100%", backgroundColor: "#f9fafb", padding: padding }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "0.5rem",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "linear-gradient(to right, #fb923c, #f97316)",
            padding: "1rem",
          }}
        >
          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: "white",
              textAlign: "center",
              margin: 0,
            }}
          >
            TOTAL PO AND PSO ATTAINMENT
          </h1>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#ffedd5" }}>
                <th
                  style={{
                    border: "1px solid #d1d5db",
                    padding: "0.75rem",
                    fontWeight: "bold",
                  }}
                >
                  Course
                </th>
                <th
                  style={{
                    border: "1px solid #d1d5db",
                    padding: "0.75rem",
                    fontWeight: "bold",
                  }}
                >
                  Class
                </th>
                <th
                  style={{
                    border: "1px solid #d1d5db",
                    padding: "0.75rem",
                    fontWeight: "bold",
                  }}
                >
                  Semester
                </th>
                {allCodes.map((po) => (
                  <th
                    key={po}
                    style={{
                      border: "1px solid #d1d5db",
                      padding: "0.75rem",
                      fontWeight: "bold",
                      textAlign: "center",
                      minWidth: "60px",
                    }}
                  >
                    {po}
                    {targets[po] > 0 && (
                      <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                        ({targets[po]})
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {classAttainments.map((cls, idx) => (
                <tr
                  key={`${cls.classId}-${cls.batch || idx}`}
                  style={{
                    backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb",
                  }}
                >
                  <td
                    style={{
                      border: "1px solid #d1d5db",
                      padding: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {cls.courseName}
                  </td>
                  <td
                    style={{
                      border: "1px solid #d1d5db",
                      padding: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {cls.className}
                  </td>
                  <td
                    style={{
                      border: "1px solid #d1d5db",
                      padding: "0.75rem",
                      textAlign: "center",
                    }}
                  >
                    {cls.courseLevel || "-"}
                  </td>
                  {allCodes.map((po) => (
                    <td
                      key={po}
                      style={{
                        border: "1px solid #d1d5db",
                        padding: "0.75rem",
                        textAlign: "center",
                        ...getCellStyle(cls.attainment[po], po),
                      }}
                    >
                      {formatValue(cls.attainment[po])}
                    </td>
                  ))}
                </tr>
              ))}
              <tr style={{ backgroundColor: "#fde047", fontWeight: "bold" }}>
                <td
                  colSpan={3}
                  style={{ border: "1px solid #d1d5db", padding: "0.75rem" }}
                >
                  Direct Attainment
                </td>
                {allCodes.map((po) => (
                  <td
                    key={po}
                    style={{
                      border: "1px solid #d1d5db",
                      padding: "0.75rem",
                      textAlign: "center",
                      ...getCellStyle(averages[po], po),
                    }}
                  >
                    {formatValue(averages[po])}
                  </td>
                ))}
              </tr>
              <tr style={{ backgroundColor: "#fde047", fontWeight: "bold" }}>
                <td
                  colSpan={3}
                  style={{ border: "1px solid #d1d5db", padding: "0.75rem" }}
                >
                  Indirect Attainment
                </td>
                {allCodes.map((po) => (
                  <td
                    key={po}
                    style={{
                      border: "1px solid #d1d5db",
                      padding: "0.75rem",
                      textAlign: "center",
                    }}
                  >
                    {formatValue(indirectAverages[po])}
                  </td>
                ))}
              </tr>
              <tr style={{ backgroundColor: "#fde047", fontWeight: "bold" }}>
                <td
                  colSpan={3}
                  style={{ border: "1px solid #d1d5db", padding: "0.75rem" }}
                >
                  Total Attainment
                </td>
                {allCodes.map((po) => {
                  const direct = averages[po];
                  const indirect =
                    indirectAverages[po] !== null ? indirectAverages[po] : 0;
                  const total =
                    direct !== null
                      ? direct * directPOWeightage +
                        indirect * indirectPOWeightage
                      : null;
                  return (
                    <td
                      key={po}
                      style={{
                        border: "1px solid #d1d5db",
                        padding: "0.75rem",
                        textAlign: "center",
                        ...getCellStyle(total, po),
                      }}
                    >
                      {formatValue(total)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// MAIN COPO DASHBOARD COMPONENT
// ============================================================================
const CoPoDashboard = ({ role = "instructor", actorId = null }) => {
  const { user } = useContext(AuthContext);

  // Core states
  const instructorId = role === "instructor" ? actorId || user?._id || "" : "";
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading Dashboard...");
  const [loadError, setLoadError] = useState(null);
  const [attainmentData, setAttainmentData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(false);

  // Filter states
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState("all");
  const [batchOptions] = useState(generateBatchOptions());
  const [programOptions, setProgramOptions] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState("all");
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedTest, setSelectedTest] = useState("all");
  const [indirectAttainmentData, setIndirectAttainmentData] = useState([]);

  // COPO Config weightage values (defaults: 0.9 for direct, 0.1 for indirect)
  const [copoConfig, setCopoConfig] = useState({
    directPOWeightage: 0.9,
    indirectPOWeightage: 0.1,
  });

  // Export states
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState("");
  const dashboardContentRef = useRef(null);

  // Ref to skip reset when course is directly selected
  const skipSemesterResetRef = useRef(false);

  // Ref to track which batches have been fetched (for incremental fetching)
  const fetchedBatchesRef = useRef(new Set());
  const currentProgramRef = useRef(null);

  // -------- FETCH PROGRAMS --------
  const fetchPrograms = useCallback(async () => {
    if (!user || !user.instituteId) return;
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/program/get-all/${user.instituteId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        const programs = (data.programs || data || []).map((p) => ({
          value: p._id,
          label: p.name || p.programName,
        }));
        setProgramOptions(programs);
      }
    } catch (err) {
      swalError("Failed to fetch programs");
    }
  }, [user]);

  // -------- FETCH INSTITUTE CONFIG (for COPO weightage settings) --------
  const fetchInstituteConfig = useCallback(async () => {
    if (!user || !user.instituteId) return;
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get/${user.instituteId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.COPOConfig) {
          setCopoConfig({
            directPOWeightage: data.COPOConfig.directPOWeightage ?? 0.9,
            indirectPOWeightage: data.COPOConfig.indirectPOWeightage ?? 0.1,
          });
        }
      }
    } catch (err) {
      console.error("Failed to fetch institute config:", err);
      // Keep default values on error
    }
  }, [user]);

  // Initial fetch for programs and institute config
  useEffect(() => {
    fetchPrograms();
    fetchInstituteConfig();
  }, [fetchPrograms, fetchInstituteConfig]);

  // -------- DATA FETCHING (Incremental) --------
  // Fetch data for a single batch
  const fetchBatchData = useCallback(
    async (programId, batch) => {
      // Build URL with optional instructorId query param for instructor role
      let url = `${serverUrl}/api/institute/getAttainmentDashboardDataForBatch/${user.instituteId}/${programId}/${batch.value}`;
      if (role === "instructor" && instructorId) {
        url += `?instructorId=${instructorId}`;
      }

      const response = await fetchWithAuth(url, { credentials: "include" });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch attainment data");
      }

      const data = await response.json();
      const classes =
        data.classAttainments || (Array.isArray(data) ? data : []);
      const indirectPOAttainments = data.indirectPOAttainments || [];

      // Add batch identifier to each class
      const classesWithBatch = classes.map((c) => ({
        ...c,
        batch: batch.value,
        batchLabel: batch.label,
      }));

      return {
        classes: classesWithBatch,
        indirect:
          indirectPOAttainments.length > 0
            ? {
                batch: batch.value,
                batchLabel: batch.label,
                attainments: indirectPOAttainments,
              }
            : null,
      };
    },
    [user, role, instructorId]
  );

  // Handle batch selection changes with incremental fetching
  useEffect(() => {
    if (!selectedProgram || selectedBatches.length === 0) {
      // No program or batches selected, clear data
      setAttainmentData([]);
      setIndirectAttainmentData([]);
      fetchedBatchesRef.current.clear();
      return;
    }

    const programId = selectedProgram.value;

    // If program changed, clear cache and refetch all
    if (currentProgramRef.current !== programId) {
      currentProgramRef.current = programId;
      fetchedBatchesRef.current.clear();
      setAttainmentData([]);
      setIndirectAttainmentData([]);
    }

    const selectedBatchValues = new Set(selectedBatches.map((b) => b.value));
    const fetchedBatches = fetchedBatchesRef.current;

    // Find batches to add (selected but not fetched)
    const batchesToAdd = selectedBatches.filter(
      (b) => !fetchedBatches.has(b.value)
    );

    // Find batches to remove (fetched but no longer selected)
    const batchesToRemove = [...fetchedBatches].filter(
      (bv) => !selectedBatchValues.has(bv)
    );

    // Remove data for deselected batches
    if (batchesToRemove.length > 0) {
      setAttainmentData((prev) =>
        prev.filter((c) => !batchesToRemove.includes(c.batch))
      );
      setIndirectAttainmentData((prev) =>
        prev.filter((ia) => !batchesToRemove.includes(ia.batch))
      );
      batchesToRemove.forEach((bv) => fetchedBatches.delete(bv));
    }

    // Fetch only new batches
    if (batchesToAdd.length > 0) {
      const fetchNewBatches = async () => {
        try {
          setLoading(true);
          setLoadingMessage(
            `Loading ${batchesToAdd.length} batch${
              batchesToAdd.length > 1 ? "es" : ""
            }...`
          );
          setLoadError(null);

          const newClasses = [];
          const newIndirect = [];

          for (const batch of batchesToAdd) {
            const result = await fetchBatchData(programId, batch);
            newClasses.push(...result.classes);
            if (result.indirect) {
              newIndirect.push(result.indirect);
            }
            // Mark as fetched
            fetchedBatches.add(batch.value);
          }

          // Apply instructor filter if needed
          let filteredNewClasses = newClasses;
          if (role === "instructor" && instructorId) {
            filteredNewClasses = newClasses.filter(
              (c) => c.instructorId === instructorId
            );
          }

          // Append new data to existing
          setAttainmentData((prev) => [...prev, ...filteredNewClasses]);
          setIndirectAttainmentData((prev) => [...prev, ...newIndirect]);
        } catch (err) {
          console.error("Error fetching batch data:", err);
          swalError("Failed to fetch attainment data");
          setLoadError(err.message);
        } finally {
          setLoading(false);
          setIsInitialLoad(false);
        }
      };

      fetchNewBatches();
    }
  }, [selectedProgram, selectedBatches, fetchBatchData, role, instructorId]);

  // -------- FILTER OPTIONS --------
  const filterOptions = useMemo(() => {
    // Semesters (from courseLevel in API response)
    const semesterSet = new Set();
    attainmentData.forEach((item) => {
      if (item.courseLevel) {
        semesterSet.add(item.courseLevel);
      }
    });

    // Courses (filtered by semester if selected)
    const courseMap = new Map();
    attainmentData.forEach((item) => {
      if (item.courseId && item.courseName) {
        // Only include courses matching selected semester
        if (
          selectedSemester === "all" ||
          item.courseLevel === selectedSemester
        ) {
          courseMap.set(item.courseId, {
            name: item.courseName,
            code: item.courseCode,
          });
        }
      }
    });

    // Classes (filtered by course)
    const classMap = new Map();
    attainmentData.forEach((item) => {
      if (item.classId && item.className) {
        if (selectedCourse === "all" || item.courseId === selectedCourse) {
          classMap.set(item.classId, item.className);
        }
      }
    });

    // Tests (filtered by class)
    const testMap = new Map();
    if (selectedClass !== "all") {
      const classData = attainmentData.find((c) => c.classId === selectedClass);
      if (classData?.testAttainments) {
        classData.testAttainments.forEach((test) => {
          testMap.set(test._id, test.testName);
        });
      }
    }

    // Instructors (admin only)
    const instructorMap = new Map();
    if (role === "admin") {
      attainmentData.forEach((item) => {
        if (item.instructorId && item.instructorName) {
          instructorMap.set(item.instructorId, item.instructorName);
        }
      });
    }

    return {
      semesters: Array.from(semesterSet).sort(),
      courses: Array.from(courseMap.entries()).map(([id, info]) => ({
        value: id,
        label: info.name,
        code: info.code,
      })),
      classes: Array.from(classMap.entries()).map(([id, name]) => ({
        value: id,
        label: name,
      })),
      tests: Array.from(testMap.entries()).map(([id, name]) => ({
        value: id,
        label: name,
      })),
      instructors: Array.from(instructorMap.entries()).map(([id, name]) => ({
        value: id,
        label: name,
      })),
    };
  }, [attainmentData, selectedSemester, selectedCourse, selectedClass, role]);

  // Auto-select batch (current year by default)
  // Note: No auto-select for batches - user must select manually

  // Reset dependent filters when program or batches changes
  useEffect(() => {
    setSelectedSemester("all");
    setSelectedCourse("all");
    setSelectedClass("all");
    setSelectedTest("all");
  }, [selectedProgram, selectedBatches]);

  // Reset course/class/test when semester changes
  useEffect(() => {
    // Skip reset if triggered by direct course selection
    if (skipSemesterResetRef.current) {
      skipSemesterResetRef.current = false;
      return;
    }
    setSelectedCourse("all");
    setSelectedClass("all");
    setSelectedTest("all");
  }, [selectedSemester]);

  useEffect(() => {
    setSelectedClass("all");
    setSelectedTest("all");
  }, [selectedCourse]);

  useEffect(() => {
    setSelectedTest("all");
  }, [selectedClass]);

  // -------- FILTER HANDLERS --------
  const handleBatchChange = useCallback((selected) => {
    setSelectedBatches(selected || []);
  }, []);

  const handleCourseChange = useCallback(
    (courseId) => {
      setSelectedCourse(courseId);
      if (courseId !== "all") {
        // Find the course and auto-select its semester if not already selected
        const courseData = attainmentData.find((c) => c.courseId === courseId);
        if (
          courseData &&
          courseData.courseLevel &&
          selectedSemester === "all"
        ) {
          // Set flag to skip reset in useEffect when semester changes
          skipSemesterResetRef.current = true;
          setSelectedSemester(courseData.courseLevel);
        }
      }
    },
    [attainmentData, selectedSemester]
  );

  const handleSemesterClick = useCallback((semesterData) => {
    setSelectedSemester(semesterData.semester);
  }, []);

  const handleCourseClick = useCallback((course) => {
    setSelectedCourse(course.courseId);
  }, []);

  const handleClassChange = useCallback(
    (classId) => {
      setSelectedClass(classId);
      if (classId !== "all") {
        // Find the class and auto-select its course
        const classData = attainmentData.find((c) => c.classId === classId);
        if (classData && classData.courseId) {
          setSelectedCourse(classData.courseId);
        }
      }
    },
    [attainmentData]
  );

  const clearFilters = useCallback(() => {
    setSelectedProgram(null);
    setSelectedBatches([]);
    setSelectedSemester("all");
    setSelectedInstructor("all");
    setSelectedCourse("all");
    setSelectedClass("all");
    setSelectedTest("all");
    setAttainmentData([]);
  }, []);

  // -------- FILTERED DATA --------
  // Data is already filtered by program and batch at API level
  // Here we just apply local filters (semester, instructor, course, class)
  const filteredData = useMemo(() => {
    let filtered = [...attainmentData];

    // Filter by semester (courseLevel)
    if (selectedSemester !== "all") {
      filtered = filtered.filter((c) => c.courseLevel === selectedSemester);
    }
    if (role === "admin" && selectedInstructor !== "all") {
      filtered = filtered.filter((c) => c.instructorId === selectedInstructor);
    }
    if (selectedCourse !== "all") {
      filtered = filtered.filter((c) => c.courseId === selectedCourse);
    }
    if (selectedClass !== "all") {
      filtered = filtered.filter((c) => c.classId === selectedClass);
    }

    return filtered;
  }, [
    attainmentData,
    selectedSemester,
    selectedInstructor,
    selectedCourse,
    selectedClass,
    role,
  ]);

  // -------- COMPUTED VALUES --------
  const semesterAggregation = useMemo(() => {
    if (selectedSemester !== "all") return null;
    const semesterMap = new Map();

    filteredData.forEach((cls) => {
      const key = cls.courseLevel;
      if (!key) return; // Skip if no courseLevel
      if (!semesterMap.has(key)) {
        semesterMap.set(key, {
          semester: key,
          totalAttainment: 0,
          count: 0,
        });
      }
      const sem = semesterMap.get(key);
      sem.totalAttainment += cls.overallAverage || 0;
      sem.count += 1;
    });

    return Array.from(semesterMap.values())
      .map((s) => ({
        ...s,
        avgAttainment: s.count > 0 ? s.totalAttainment / s.count : 0,
      }))
      .sort((a, b) => a.semester.localeCompare(b.semester));
  }, [filteredData, selectedSemester]);

  // Course aggregation (when semester is selected but course is not)
  const courseAggregation = useMemo(() => {
    if (selectedSemester === "all" || selectedCourse !== "all") return null;
    const courseMap = new Map();

    filteredData.forEach((cls) => {
      const key = cls.courseId;
      if (!courseMap.has(key)) {
        courseMap.set(key, {
          courseId: cls.courseId,
          courseName: cls.courseName,
          courseCode: cls.courseCode,
          totalAttainment: 0,
          count: 0,
        });
      }
      const course = courseMap.get(key);
      course.totalAttainment += cls.overallAverage || 0;
      course.count += 1;
    });

    return Array.from(courseMap.values()).map((c) => ({
      ...c,
      avgAttainment: c.count > 0 ? c.totalAttainment / c.count : 0,
    }));
  }, [filteredData, selectedSemester, selectedCourse]);

  const selectedClassData = useMemo(() => {
    if (selectedClass === "all") return null;
    return filteredData.find((c) => c.classId === selectedClass);
  }, [filteredData, selectedClass]);

  const programOutcomes = useMemo(() => {
    const first = filteredData[0];
    return first?.programOutcomes || [];
  }, [filteredData]);

  const psOutcomes = useMemo(() => {
    const first = filteredData[0];
    return first?.PSOutcomes || [];
  }, [filteredData]);

  // PO/PSO Chart data for course view (when specific course is selected)
  const poChartData = useMemo(() => {
    if (selectedCourse === "all" || !filteredData || filteredData.length === 0)
      return [];

    const allPOs = [...programOutcomes, ...psOutcomes];
    if (allPOs.length === 0) return [];

    return allPOs.map((po) => {
      const values = filteredData
        .map((cls) => {
          const mapping = cls.courseCOPOMapping || [];
          const targetPO = po.code.startsWith("PSO")
            ? po.code.replace("PSO", "PS")
            : po.code;
          const weights = mapping
            .filter((item) => {
              const itemPO = (item.POCode || "")
                .replace("PSO", "PS")
                .replace(" ", "");
              return itemPO === targetPO;
            })
            .map((item) => item.weight);

          if (weights.length === 0) return null;
          const avgWeight = weights.reduce((a, b) => a + b, 0) / weights.length;
          return (cls.overallAverage || 0) * (avgWeight / 3);
        })
        .filter((v) => v !== null);

      const avgAttainment =
        values.length > 0
          ? values.reduce((a, b) => a + b, 0) / values.length
          : 0;

      return {
        code: po.code,
        description: po.description || "",
        attainment: avgAttainment,
        target: po.targetAttainment || 0,
      };
    });
  }, [filteredData, programOutcomes, psOutcomes, selectedCourse]);

  // Group data by batch for separate table display
  const dataGroupedByBatch = useMemo(() => {
    if (selectedBatches.length <= 1) return null;
    const groups = {};
    filteredData.forEach((cls) => {
      const batch = cls.batch || "Unknown";
      if (!groups[batch]) groups[batch] = [];
      groups[batch].push(cls);
    });
    // Sort by batch year (most recent first)
    return Object.entries(groups).sort(
      (a, b) => parseInt(b[0]) - parseInt(a[0])
    );
  }, [filteredData, selectedBatches]);

  // PO/PSO Multi-bar chart data (one bar per batch for each PO)
  const poMultiBarChartData = useMemo(() => {
    if (
      selectedCourse === "all" ||
      !filteredData ||
      filteredData.length === 0 ||
      selectedBatches.length <= 1
    )
      return null;

    const allPOs = [...programOutcomes, ...psOutcomes];
    if (allPOs.length === 0) return null;

    // Get unique batches from filtered data
    const batches = [...new Set(filteredData.map((c) => c.batch))].sort(
      (a, b) => parseInt(a) - parseInt(b)
    );

    // Calculate attainment per batch per PO
    const datasets = batches.map((batch, idx) => {
      const batchClasses = filteredData.filter((c) => c.batch === batch);
      const colors = [
        "rgba(59, 130, 246, 0.8)", // Blue
        "rgba(34, 197, 94, 0.8)", // Green
        "rgba(251, 191, 36, 0.8)", // Yellow
        "rgba(239, 68, 68, 0.8)", // Red
        "rgba(168, 85, 247, 0.8)", // Purple
      ];

      const data = allPOs.map((po) => {
        const values = batchClasses
          .map((cls) => {
            const mapping = cls.courseCOPOMapping || [];
            const targetPO = po.code.startsWith("PSO")
              ? po.code.replace("PSO", "PS")
              : po.code;
            const weights = mapping
              .filter((item) => {
                const itemPO = (item.POCode || "")
                  .replace("PSO", "PS")
                  .replace(" ", "");
                return itemPO === targetPO;
              })
              .map((item) => item.weight);

            if (weights.length === 0) return null;
            const avgWeight =
              weights.reduce((a, b) => a + b, 0) / weights.length;
            return (cls.overallAverage || 0) * (avgWeight / 3);
          })
          .filter((v) => v !== null);

        return values.length > 0
          ? values.reduce((a, b) => a + b, 0) / values.length
          : 0;
      });

      return {
        label: `Batch ${batch}`,
        data: data.map((v) => parseFloat(v?.toFixed(2))),
        backgroundColor: colors[idx % colors.length],
        borderColor: colors[idx % colors.length].replace("0.8", "1"),
        borderWidth: 2,
        borderRadius: 4,
      };
    });

    return {
      labels: allPOs.map((po) => po.code),
      datasets,
      targets: allPOs.map((po) => po.targetAttainment || 0),
      poDescriptions: allPOs.map((po) => ({
        code: po.code,
        description: po.description || "",
        target: po.targetAttainment || 0,
      })),
    };
  }, [
    filteredData,
    programOutcomes,
    psOutcomes,
    selectedCourse,
    selectedBatches,
  ]);

  const hasActiveFilters =
    selectedSemester !== "all" ||
    selectedCourse !== "all" ||
    selectedClass !== "all" ||
    selectedTest !== "all" ||
    (role === "admin" && selectedInstructor !== "all");

  // -------- EXPORT FUNCTIONS --------

  // Export to Excel function
  const exportToExcel = async () => {
    if (filteredData.length === 0) {
      swalError("No data to export. Please select a program and batch first.");
      return;
    }

    try {
      setExporting(true);
      setExportProgress("Preparing Excel file...");

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "GradeMe AI";
      workbook.created = new Date();

      // Common styles
      const headerStyle = {
        font: { bold: true, color: { argb: "FFFFFF" }, size: 11 },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "1E3A5F" },
        },
        alignment: { horizontal: "center", vertical: "middle" },
        border: {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        },
      };

      const summaryRowStyle = {
        font: { bold: true, color: { argb: "000000" } },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FDE047" },
        },
        border: {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        },
      };

      const standardPOs = [
        "PO1",
        "PO2",
        "PO3",
        "PO4",
        "PO5",
        "PO6",
        "PO7",
        "PO8",
        "PO9",
        "PO10",
        "PO11",
        "PO12",
      ];
      const standardPSOs = ["PSO1", "PSO2", "PSO3"];
      const allCodes = [...standardPOs, ...standardPSOs];

      // Group data by batch
      const batchGroups = {};
      filteredData.forEach((cls) => {
        const batchKey = cls.batch || "Unknown";
        if (!batchGroups[batchKey]) {
          batchGroups[batchKey] = [];
        }
        batchGroups[batchKey].push(cls);
      });

      const batches = Object.keys(batchGroups).sort(
        (a, b) => parseInt(a) - parseInt(b)
      );

      // Create a summary sheet first
      setExportProgress("Creating summary sheet...");
      const summarySheet = workbook.addWorksheet("Summary");

      // Summary title
      summarySheet.mergeCells("A1:F1");
      const summaryTitle = summarySheet.getCell("A1");
      summaryTitle.value = `CO-PO Attainment Report - ${
        selectedProgram?.label || "Program"
      }`;
      summaryTitle.font = { bold: true, size: 16, color: { argb: "1E3A5F" } };
      summaryTitle.alignment = { horizontal: "center" };

      summarySheet.mergeCells("A2:F2");
      summarySheet.getCell(
        "A2"
      ).value = `Generated on: ${new Date().toLocaleDateString("en-IN", {
        dateStyle: "full",
      })}`;
      summarySheet.getCell("A2").font = { italic: true, size: 10 };
      summarySheet.getCell("A2").alignment = { horizontal: "center" };

      summarySheet.mergeCells("A3:F3");
      summarySheet.getCell("A3").value = `Batches: ${selectedBatches
        .map((b) => b.label)
        .join(", ")}`;
      summarySheet.getCell("A3").font = { size: 11 };
      summarySheet.getCell("A3").alignment = { horizontal: "center" };

      // Summary table
      summarySheet.addRow([]);
      const summaryHeaders = summarySheet.addRow([
        "Batch",
        "Total Courses",
        "Total Classes",
        "Avg CO Attainment",
        "Classes with Data",
        "Report Status",
      ]);
      summaryHeaders.eachCell((cell) => Object.assign(cell, headerStyle));

      batches.forEach((batch) => {
        const batchData = batchGroups[batch];
        const uniqueCourses = new Set(batchData.map((c) => c.courseId)).size;
        const avgAttainment =
          batchData.reduce((sum, c) => sum + (c.overallAverage || 0), 0) /
          batchData.length;
        const classesWithData = batchData.filter(
          (c) => c.attainment && c.attainment.length > 0
        ).length;

        const row = summarySheet.addRow([
          batch,
          uniqueCourses,
          batchData.length,
          avgAttainment?.toFixed(2),
          `${classesWithData}/${batchData.length}`,
          classesWithData === batchData.length ? "Complete" : "Partial",
        ]);
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (colNumber >= 3) cell.alignment = { horizontal: "center" };
        });
      });

      summarySheet.columns.forEach((col, i) => {
        col.width = i === 0 ? 12 : 18;
      });

      // Create sheet for each batch
      for (const batch of batches) {
        setExportProgress(`Creating Batch ${batch} sheet...`);
        const batchData = batchGroups[batch];
        const sheetName = `Batch ${batch}`;
        const sheet = workbook.addWorksheet(sheetName);

        let currentRow = 1;

        // Batch Header
        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        const batchTitle = sheet.getCell(`A${currentRow}`);
        batchTitle.value = `Batch ${batch} - CO-PO Attainment Report`;
        batchTitle.font = { bold: true, size: 14, color: { argb: "1E3A5F" } };
        batchTitle.alignment = { horizontal: "center" };
        currentRow += 2;

        // ============ SECTION 1: CO ATTAINMENT TABLE ============
        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        const coTitle = sheet.getCell(`A${currentRow}`);
        coTitle.value = "📊 CO ATTAINMENT (Course Outcome Attainment)";
        coTitle.font = { bold: true, size: 12, color: { argb: "1E3A5F" } };
        coTitle.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "E5E7EB" },
        };
        currentRow++;

        // CO Headers
        const coHeaders = [
          "Course",
          "Course Code",
          "Class",
          "Semester",
          "CO Level",
          "Internal",
          "External",
          "Direct CO",
          "Indirect CO",
          "Total CO",
        ];
        const coHeaderRow = sheet.addRow(coHeaders);
        coHeaderRow.eachCell((cell) => Object.assign(cell, headerStyle));
        currentRow++;

        // CO Data
        batchData.forEach((cls) => {
          if (cls.attainment && cls.attainment.length > 0) {
            cls.attainment.forEach((co, idx) => {
              const row = sheet.addRow([
                idx === 0 ? cls.courseName : "",
                idx === 0 ? cls.courseCode : "",
                idx === 0 ? cls.className : "",
                idx === 0 ? cls.courseLevel : "",
                co.coLevel || "-",
                (co.internalAttainment || 0)?.toFixed(2),
                (co.externalAttainment || 0)?.toFixed(2),
                (co.directCOAttainment || 0)?.toFixed(2),
                (co.indirectCOAttainment || 0)?.toFixed(2),
                (co.totalCOAttainment || 0)?.toFixed(2),
              ]);
              row.eachCell((cell, colNumber) => {
                cell.border = {
                  top: { style: "thin" },
                  left: { style: "thin" },
                  bottom: { style: "thin" },
                  right: { style: "thin" },
                };
                if (colNumber >= 6) cell.alignment = { horizontal: "center" };
              });
              currentRow++;
            });

            // Class summary row
            const classAvg = cls.overallAverage || 0;
            const summaryRow = sheet.addRow([
              cls.courseName + " - Total",
              cls.courseCode,
              cls.className,
              cls.courseLevel,
              "Overall",
              "-",
              "-",
              "-",
              "-",
              classAvg?.toFixed(2),
            ]);
            summaryRow.eachCell((cell) => Object.assign(cell, summaryRowStyle));
            currentRow++;
          }
        });

        currentRow += 2;

        // ============ SECTION 2: PO/PSO ATTAINMENT TABLE ============
        sheet.mergeCells(`A${currentRow}:R${currentRow}`);
        const poTitle = sheet.getCell(`A${currentRow}`);
        poTitle.value = "📈 PO & PSO ATTAINMENT (Program Outcome Attainment)";
        poTitle.font = { bold: true, size: 12, color: { argb: "1E3A5F" } };
        poTitle.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "E5E7EB" },
        };
        currentRow++;

        // PO Headers
        const poHeaders = ["Course", "Class", "Semester", ...allCodes];
        const poHeaderRow = sheet.addRow(poHeaders);
        poHeaderRow.eachCell((cell) => Object.assign(cell, headerStyle));
        currentRow++;

        // PO Data for each class
        const classAttainments = [];
        batchData.forEach((cls) => {
          const mapping = cls.courseCOPOMapping || [];
          const overallAvg = cls.overallAverage || 0;

          const poValues = allCodes.map((poCode) => {
            const targetPO = poCode.startsWith("PSO")
              ? poCode.replace("PSO", "PS")
              : poCode;
            const weights = mapping
              .filter((item) => {
                const itemPO = (item.POCode || "")
                  .replace("PSO", "PS")
                  .replace(" ", "");
                return itemPO === targetPO;
              })
              .map((item) => item.weight);

            if (weights.length === 0) return null;
            const avgWeight =
              weights.reduce((a, b) => a + b, 0) / weights.length;
            return overallAvg * (avgWeight / 3);
          });

          classAttainments.push({ cls, poValues });

          const row = sheet.addRow([
            cls.courseName || "-",
            cls.className || "-",
            cls.courseLevel || "-",
            ...poValues.map((v) => (v !== null ? v?.toFixed(2) : "-")),
          ]);
          row.eachCell((cell, colNumber) => {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
            if (colNumber > 3) {
              cell.alignment = { horizontal: "center" };
              const val = parseFloat(cell.value);
              const poIndex = colNumber - 4; // 0-indexed PO position
              const poCode = allCodes[poIndex];

              // Get target for this PO/PSO from programOutcomes or psOutcomes
              let target = 2.0; // Default target
              if (poCode) {
                const po = programOutcomes.find((p) => p.code === poCode);
                const pso = psOutcomes.find((p) => p.code === poCode);
                if (po && po.target) target = po.target;
                else if (pso && pso.target) target = pso.target;
              }

              if (!isNaN(val) && val > 0) {
                // Green for target attained, Red for not attained
                if (val >= target) {
                  cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "C6EFCE" }, // Light green
                  };
                } else {
                  cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFC7CE" }, // Light red
                  };
                }
              }
            }
          });
          currentRow++;
        });

        // Calculate Direct Attainment averages
        const directAverages = allCodes.map((_, i) => {
          const values = classAttainments
            .map((ca) => ca.poValues[i])
            .filter((v) => v !== null);
          return values.length > 0
            ? values.reduce((a, b) => a + b, 0) / values.length
            : null;
        });

        // Direct Attainment row
        const directRow = sheet.addRow([
          "Direct Attainment",
          "",
          "",
          ...directAverages.map((v) => (v !== null ? v?.toFixed(2) : "-")),
        ]);
        directRow.eachCell((cell) => Object.assign(cell, summaryRowStyle));
        currentRow++;

        // Get indirect attainment for this batch
        const batchIndirect = indirectAttainmentData.find(
          (ia) => String(ia.batch) === String(batch)
        );
        const indirectAverages = allCodes.map((poCode) => {
          if (!batchIndirect || !batchIndirect.attainments) return null;
          const targetPO = poCode.startsWith("PSO")
            ? poCode.replace("PSO", "PS")
            : poCode;
          const found = batchIndirect.attainments.find((a) => {
            const itemPO = (a.poCode || "")
              .replace("PSO", "PS")
              .replace(" ", "");
            return itemPO === targetPO || a.poCode === poCode;
          });
          return found ? parseFloat(found.attainment) : null;
        });

        // Indirect Attainment row
        const indirectRow = sheet.addRow([
          "Indirect Attainment",
          "",
          "",
          ...indirectAverages.map((v) => (v !== null ? v?.toFixed(2) : "-")),
        ]);
        indirectRow.eachCell((cell) => Object.assign(cell, summaryRowStyle));
        currentRow++;

        // Total Attainment row
        const totalAverages = allCodes.map((_, i) => {
          const direct = directAverages[i];
          const indirect =
            indirectAverages[i] !== null ? indirectAverages[i] : 0;
          return direct !== null ? direct * 0.8 + indirect * 0.2 : null;
        });

        const totalRow = sheet.addRow([
          "Total Attainment",
          "",
          "",
          ...totalAverages.map((v) => (v !== null ? v?.toFixed(2) : "-")),
        ]);
        totalRow.eachCell((cell) => {
          Object.assign(cell, summaryRowStyle);
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "F97316" },
          };
          cell.font = { bold: true, color: { argb: "FFFFFF" } };
        });

        // Set column widths
        sheet.getColumn(1).width = 30;
        sheet.getColumn(2).width = 15;
        sheet.getColumn(3).width = 15;
        for (let i = 4; i <= 18; i++) {
          if (sheet.getColumn(i)) sheet.getColumn(i).width = 8;
        }
      }

      setExportProgress("Generating file...");

      // Generate and download
      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `CO-PO_Attainment_${
        selectedProgram?.label?.replace(/\s+/g, "_") || "Report"
      }_${new Date().toISOString().split("T")[0]}.xlsx`;
      saveAs(new Blob([buffer]), fileName);

      setExportProgress("");
    } catch (err) {
      console.error("Excel export error:", err);
      swalError("Failed to export Excel file. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // -------- REACT-SELECT STYLES --------
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "42px",
      borderColor: state.isFocused ? "var(--color-1)" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(99, 102, 241, 0.2)" : "none",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "var(--color-5)",
      borderRadius: "4px",
    }),
    multiValueLabel: (base) => ({ ...base, color: "black", fontWeight: 500 }),
    multiValueRemove: (base) => ({
      ...base,
      color: "black",
      "&:hover": { backgroundColor: "var(--color-1)", color: "white" },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--color-1)"
        : state.isFocused
        ? "rgba(99, 102, 241, 0.1)"
        : "white",
      color: state.isSelected ? "white" : "#1f2937",
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // Initial loading state - full screen loader
  if (loading && isInitialLoad && !attainmentData.length) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>{loadingMessage}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError && !attainmentData.length) {
    return (
      <div className="dashboard">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Dashboard</h2>
          <p>{loadError}</p>
          <button
            onClick={() => {
              fetchedBatchesRef.current.clear();
              setLoadError(null);
              // Trigger refetch by resetting program ref
              currentProgramRef.current = null;
              setSelectedBatches([...selectedBatches]);
            }}
            className="retry-btn"
          >
            <span>🔄</span> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="copo-home-wrapper">
      {/* Updating indicator (shown when updating data, not initial load) */}
      {loading && !isInitialLoad && (
        <div className="updating-indicator">
          <div className="updating-spinner" />
          <span>Loading Data...</span>
        </div>
      )}

      <div className="dashboard">
        {/* Header */}
        <motion.div
          className="dashboard-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="header-content">
            <h1>
              📊 {role === "admin" ? "Institute" : "Instructor"} Accreditation
              Dashboard
            </h1>
            <p>Complete Analysis of CO-PO and PSO Accreditation</p>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          className="filters-panel"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="filters-header">
            <h3>🎯 Smart Filters</h3>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="clear-filters-btn">
                Clear Filters
              </button>
            )}
            {/* Export Button */}
            <motion.button
              className="export-main-btn"
              onClick={exportToExcel}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={exporting || filteredData.length === 0}
            >
              {exporting ? (
                <>
                  <span className="export-spinner"></span>
                  {exportProgress || "Exporting..."}
                </>
              ) : (
                <>
                  <span>📊</span> Export to Excel
                </>
              )}
            </motion.button>
          </div>

          <div
            className={`filters-grid ${
              role === "admin" ? "admin-copo-filters-grid" : "copo-filters-grid"
            }`}
          >
            {/* Program */}
            <div className="filter-group">
              <label>📋 Program *</label>
              <Select
                value={selectedProgram}
                onChange={setSelectedProgram}
                options={programOptions}
                styles={selectStyles}
                placeholder="Select program..."
                isClearable={false}
                isDisabled={programOptions.length === 0}
                menuPortalTarget={document.body}
              />
            </div>

            {/* Batch (Year) - Multi-select */}
            <div className="filter-group">
              <label>📅 Batch Year *</label>
              <Select
                value={selectedBatches}
                onChange={handleBatchChange}
                options={batchOptions}
                styles={selectStyles}
                placeholder="Select batch years..."
                isMulti
                isClearable={false}
                isDisabled={!selectedProgram}
                menuPortalTarget={document.body}
              />
            </div>

            {/* Semester */}
            <div className="filter-group">
              <label>📆 Semester</label>
              <select
                value={selectedSemester}
                onChange={(e) => setSelectedSemester(e.target.value)}
                disabled={filterOptions.semesters.length === 0}
              >
                <option value="all">All Semesters</option>
                {filterOptions.semesters.map((sem) => (
                  <option key={sem} value={sem}>
                    {sem}
                  </option>
                ))}
              </select>
            </div>

            {/* Course */}
            <div className="filter-group">
              <label>📚 Course</label>
              <select
                value={selectedCourse}
                onChange={(e) => handleCourseChange(e.target.value)}
                disabled={filterOptions.courses.length === 0}
              >
                <option value="all">All Courses</option>
                {filterOptions.courses.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>🏫 Class</label>
              <select
                value={selectedClass}
                onChange={(e) => handleClassChange(e.target.value)}
                disabled={filterOptions.classes.length === 0}
              >
                <option value="all">All Classes</option>
                {filterOptions.classes.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Content based on filter state */}
      <div ref={dashboardContentRef}>
        {selectedSemester === "all" && selectedCourse === "all" ? (
          // VIEW 1: All Semesters - Semester Bar Chart + PO/PSO Table
          <>
            <SemesterAttainmentChart
              semesters={semesterAggregation || []}
              onSemesterClick={handleSemesterClick}
            />
            {/* Show separate tables per batch if multiple batches selected */}
            {dataGroupedByBatch ? (
              dataGroupedByBatch.map(([batch, batchData]) => (
                <div key={batch} style={{ marginBottom: "2rem" }}>
                  <div style={{ padding: "0 1rem" }}>
                    <motion.div
                      className="class-attainment-section"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="batch-header"
                      >
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            background: "rgba(255, 255, 255, 0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.5rem",
                          }}
                        >
                          🎓
                        </div>
                        <div>
                          <h3
                            style={{
                              color: "white",
                              margin: 0,
                              fontSize: "1.35rem",
                              fontWeight: 700,
                              letterSpacing: "0.5px",
                            }}
                          >
                            Batch {batch}
                          </h3>
                          <p
                            style={{
                              color: "rgba(255, 255, 255, 0.75)",
                              margin: "0.25rem 0 0 0",
                              fontSize: "0.875rem",
                            }}
                          >
                            {batchData.length} class
                            {batchData.length !== 1 ? "es" : ""} in this batch
                          </p>
                        </div>
                      </motion.div>
                      <POPSOAttainmentTable
                        classesData={batchData}
                        programOutcomes={programOutcomes}
                        psOutcomes={psOutcomes}
                        indirectAttainmentData={indirectAttainmentData}
                        directPOWeightage={copoConfig.directPOWeightage}
                        indirectPOWeightage={copoConfig.indirectPOWeightage}
                      />
                    </motion.div>
                  </div>
                </div>
              ))
            ) : (
              <POPSOAttainmentTable
                classesData={filteredData}
                programOutcomes={programOutcomes}
                psOutcomes={psOutcomes}
                padding="1.5rem"
                indirectAttainmentData={indirectAttainmentData}
                directPOWeightage={copoConfig.directPOWeightage}
                indirectPOWeightage={copoConfig.indirectPOWeightage}
              />
            )}
          </>
        ) : selectedSemester !== "all" && selectedCourse === "all" ? (
          // VIEW 1.5: Semester Selected - Course Bar Chart + PO/PSO Table
          <>
            <CourseAttainmentChart
              courses={courseAggregation || []}
              onCourseClick={handleCourseClick}
            />
            {/* Show separate tables per batch if multiple batches selected */}
            {dataGroupedByBatch ? (
              dataGroupedByBatch.map(([batch, batchData]) => (
                <div key={batch} style={{ marginBottom: "2rem" }}>
                  <div style={{ padding: "0 1rem" }}>
                    <motion.div
                      className="class-attainment-section"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="batch-header"
                      >
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            background: "rgba(255, 255, 255, 0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.5rem",
                          }}
                        >
                          🎓
                        </div>
                        <div>
                          <h3
                            style={{
                              color: "white",
                              margin: 0,
                              fontSize: "1.35rem",
                              fontWeight: 700,
                              letterSpacing: "0.5px",
                            }}
                          >
                            Batch {batch}
                          </h3>
                          <p
                            style={{
                              color: "rgba(255, 255, 255, 0.75)",
                              margin: "0.25rem 0 0 0",
                              fontSize: "0.875rem",
                            }}
                          >
                            {batchData.length} class
                            {batchData.length !== 1 ? "es" : ""} in this batch
                          </p>
                        </div>
                      </motion.div>
                      <POPSOAttainmentTable
                        classesData={batchData}
                        programOutcomes={programOutcomes}
                        psOutcomes={psOutcomes}
                        indirectAttainmentData={indirectAttainmentData}
                        directPOWeightage={copoConfig.directPOWeightage}
                        indirectPOWeightage={copoConfig.indirectPOWeightage}
                      />
                    </motion.div>
                  </div>
                </div>
              ))
            ) : (
              <POPSOAttainmentTable
                classesData={filteredData}
                programOutcomes={programOutcomes}
                psOutcomes={psOutcomes}
                padding="1.5rem"
                indirectAttainmentData={indirectAttainmentData}
                directPOWeightage={copoConfig.directPOWeightage}
                indirectPOWeightage={copoConfig.indirectPOWeightage}
              />
            )}
          </>
        ) : selectedClass === "all" ? (
          // VIEW 2: Specific Course - Aggregated view + PO/PSO Chart
          <>
            {/* Show multi-bar chart if multiple batches, else single bar chart */}
            {poMultiBarChartData ? (
              <POPSOMultiBarChart
                chartData={poMultiBarChartData}
                title="PO & PSO Attainment by Batch"
              />
            ) : (
              <POPSOAttainmentChart
                poData={poChartData}
                title="PO & PSO Attainment"
              />
            )}

            {/* Show tables grouped by batch if multiple batches selected */}
            {dataGroupedByBatch ? (
              dataGroupedByBatch.map(([batch, batchData]) => (
                <div key={batch} style={{ marginBottom: "2rem" }}>
                  <div style={{ padding: "0 1rem" }}>
                    <motion.div
                      className="class-attainment-section"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="batch-header"
                      >
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            background: "rgba(255, 255, 255, 0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.5rem",
                          }}
                        >
                          🎓
                        </div>
                        <div>
                          <h3
                            style={{
                              color: "white",
                              margin: 0,
                              fontSize: "1.35rem",
                              fontWeight: 700,
                              letterSpacing: "0.5px",
                            }}
                          >
                            Batch {batch}
                          </h3>
                          <p
                            style={{
                              color: "rgba(255, 255, 255, 0.75)",
                              margin: "0.25rem 0 0 0",
                              fontSize: "0.875rem",
                            }}
                          >
                            {batchData.length} class
                            {batchData.length !== 1 ? "es" : ""} in this batch
                          </p>
                        </div>
                      </motion.div>
                      <COAttainmentTable
                        attainment={batchData[0]?.attainment}
                        overallAverage={batchData[0]?.overallAverage}
                        courseCode={batchData[0]?.courseCode}
                      />
                      <COPOMappingTable
                        courseCOPOMapping={batchData[0]?.courseCOPOMapping}
                        courseCode={batchData[0]?.courseCode}
                      />
                      <COAttainmentSummaryTable
                        coAttainment={batchData[0]?.overallAverage}
                      />
                      <POPSOAttainmentSummaryTable
                        courseCOPOMapping={batchData[0]?.courseCOPOMapping}
                        coAttainment={batchData[0]?.overallAverage}
                      />
                      <POPSOAttainmentTable
                        classesData={batchData}
                        programOutcomes={programOutcomes}
                        psOutcomes={psOutcomes}
                        indirectAttainmentData={indirectAttainmentData}
                        directPOWeightage={copoConfig.directPOWeightage}
                        indirectPOWeightage={copoConfig.indirectPOWeightage}
                      />
                    </motion.div>
                  </div>
                </div>
              ))
            ) : (
              /* Single batch view */
              <>
                {filteredData.length > 0 && (
                  <div style={{ padding: "1rem" }}>
                    <motion.div
                      className="class-attainment-section"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <COAttainmentTable
                        attainment={filteredData[0]?.attainment}
                        overallAverage={filteredData[0]?.overallAverage}
                        courseCode={filteredData[0]?.courseCode}
                      />
                      <COPOMappingTable
                        courseCOPOMapping={filteredData[0]?.courseCOPOMapping}
                        courseCode={filteredData[0]?.courseCode}
                      />
                      <COAttainmentSummaryTable
                        coAttainment={filteredData[0]?.overallAverage}
                      />
                      <POPSOAttainmentSummaryTable
                        courseCOPOMapping={filteredData[0]?.courseCOPOMapping}
                        coAttainment={filteredData[0]?.overallAverage}
                      />
                      <POPSOAttainmentTable
                        classesData={filteredData}
                        programOutcomes={programOutcomes}
                        psOutcomes={psOutcomes}
                        indirectAttainmentData={indirectAttainmentData}
                        directPOWeightage={copoConfig.directPOWeightage}
                        indirectPOWeightage={copoConfig.indirectPOWeightage}
                      />
                    </motion.div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          // VIEW 3: Specific Class
          selectedClassData && (
            <div style={{ padding: "1rem" }}>
              <motion.div
                className="class-attainment-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <COAttainmentTable
                  attainment={selectedClassData.attainment}
                  overallAverage={selectedClassData.overallAverage}
                  courseCode={selectedClassData.courseCode}
                />
                <COPOMappingTable
                  courseCOPOMapping={selectedClassData.courseCOPOMapping}
                  courseCode={selectedClassData.courseCode}
                />
                <COAttainmentSummaryTable
                  coAttainment={selectedClassData.overallAverage}
                />
                <POPSOAttainmentSummaryTable
                  courseCOPOMapping={selectedClassData.courseCOPOMapping}
                  coAttainment={selectedClassData.overallAverage}
                />
                <POPSOAttainmentTable
                  classesData={[selectedClassData]}
                  programOutcomes={programOutcomes}
                  psOutcomes={psOutcomes}
                  indirectAttainmentData={indirectAttainmentData}
                  directPOWeightage={copoConfig.directPOWeightage}
                  indirectPOWeightage={copoConfig.indirectPOWeightage}
                />
              </motion.div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default CoPoDashboard;
